/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { useCallback, useMemo } from 'preact/hooks';

import { MenuCollectionItem } from './menuUtils';

/**
 * Hook for handling current key update due to user interaction including keyboard navigation.
 *
 * @param getPrev function to get the previous key based on the current key
 * @param getNext function to get the next key based on the current key
 * @param getFirstVisible function to get the key of the first available item
 * @param getLastVisible function to get the key of the last available item
 * @param onChange function to invoke if the current key has changed
 * @returns
 */
export function useMenuCurrentKey(
  getPrev: () => string | null,
  getNext: () => string | null,
  getFirstVisible: () => string | null,
  getLastVisible: () => string | null,
  onChange?: (detail: MenuCollectionItem) => void
) {
  const keyDownKeyFunctions = useMemo(() => {
    return {
      ArrowDown: getNext,
      ArrowUp: getPrev,
      Home: getFirstVisible,
      End: getLastVisible
    } as Record<string, (() => string | null) | undefined>;
  }, [getNext, getPrev, getFirstVisible, getLastVisible]);

  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (onChange) {
        // TODO: support horizontal layout on icon menu (left/right arrow key)
        const keyFunction = keyDownKeyFunctions[event.key];
        if (keyFunction && !event.shiftKey) {
          const key = keyFunction();
          if (key) {
            onChange({ key, reason: 'keyboard' });
          }
          event.preventDefault();
        }
      }
    },
    [onChange, keyDownKeyFunctions]
  );

  const currentKeyProps = onChange == null ? {} : { onKeyDown };
  return { currentKeyProps };
}
